import { useState, useEffect, useMemo } from "react";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Empty } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ShieldAlert, Send, Trash2, CheckCircle2, AlertTriangle, FileDown } from "lucide-react";

const CATS = [
  { v: "talking", en: "Excessive talking", fr: "Bavardage excessif" },
  { v: "abusive_language", en: "Abusive language", fr: "Langage abusif" },
  { v: "lateness", en: "Lateness", fr: "Retard" },
  { v: "homework", en: "Homework/projects not done", fr: "Devoirs / projets non faits" },
  { v: "reading", en: "Books not read", fr: "Livres non lus" },
  { v: "other", en: "Other misconduct", fr: "Autre inconduite" },
  { v: "fighting", en: "Fighting in school", fr: "Bagarre à l'école" },
  { v: "cheating", en: "Cheating", fr: "Tricherie" },
  { v: "stealing", en: "Stealing", fr: "Vol" },
  { v: "vandalism", en: "Vandalism", fr: "Vandalisme" },
];
const ACTIONS = [
  { v: "warning", en: "Warning", fr: "Avertissement" },
  { v: "suspension", en: "Suspension", fr: "Suspension" },
  { v: "expulsion", en: "Expulsion", fr: "Expulsion" },
];
const ACTION_STYLE = { warning: "bg-amber-100 text-amber-700 border-amber-300", suspension: "bg-orange-100 text-orange-700 border-orange-300", expulsion: "bg-red-100 text-red-700 border-red-300" };

export default function Discipline() {
  const { L } = useLang();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.permissions?.school_management === "manage" || user?.permissions?.academics === "manage";
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState("");
  const [detail, setDetail] = useState(null);
  const [category, setCategory] = useState("");
  const [action, setAction] = useState("");
  const [days, setDays] = useState(1);
  const [notes, setNotes] = useState("");
  const [notify, setNotify] = useState(true);
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { (async () => { try { const s = await api.get("/students"); setStudents((s || []).filter((x) => x.status === "accepted")); } catch (e) {} })(); }, []);

  const loadDetail = async (id) => { if (!id) return; try { setDetail(await api.get(`/students/${id}/disciplinary`)); } catch (e) {} };
  useEffect(() => { loadDetail(sid); /* eslint-disable-next-line */ }, [sid]);

  useEffect(() => {
    (async () => {
      if (!sid || !category) { setRec(null); return; }
      try {
        const r = await api.get("/disciplinary/recommend", { params: { student_id: sid, category, suspension_days: days } });
        setRec(r); setAction(r.recommended_action);
      } catch (e) { setRec(null); }
    })();
  }, [sid, category, days]);

  const submit = async () => {
    if (!sid || !category) { toast.error(L("Select student and category", "Choisir élève et motif")); return; }
    setBusy(true);
    try {
      await api.post("/disciplinary", { student_id: sid, category, action: isAdmin ? action : null, suspension_days: days, notes, notify: isAdmin ? notify : false });
      toast.success(isAdmin ? L("Measure recorded", "Mesure enregistrée") : L("Sent to admin", "Envoyé à l'administration"));
      setCategory(""); setNotes(""); setRec(null); setAction(""); setDays(1);
      loadDetail(sid);
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  const approve = async (r) => { try { await api.patch(`/disciplinary/${r.id}`, { action: r.recommended_action, suspension_days: r.suspension_days || 1, notify: true }); toast.success(L("Recorded & parent notified", "Enregistré & parent notifié")); loadDetail(sid); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (r) => { try { await api.del(`/disciplinary/${r.id}`); loadDetail(sid); } catch (e) { toast.error(apiErr(e)); } };
  const letter = async (r) => { try { const res = await api.raw.get(`/disciplinary/${r.id}/pdf`, { responseType: "blob" }); const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = "avis_disciplinaire.pdf"; a.click(); URL.revokeObjectURL(u); } catch (e) { toast.error(apiErr(e)); } };

  const summary = detail?.summary;
  const records = detail?.records || [];
  const catLabel = (v) => { const c = CATS.find((x) => x.v === v); return c ? L(c.en, c.fr) : v; };
  const actLabel = (v) => { const a = ACTIONS.find((x) => x.v === v); return a ? L(a.en, a.fr) : v; };
  const selectedStudent = useMemo(() => students.find((s) => s.id === sid), [students, sid]);

  return (
    <div className="animate-fade-up" data-testid="discipline">
      <div className="mb-4">
        <h2 className="font-heading font-extrabold text-2xl flex items-center gap-2"><ShieldAlert className="h-6 w-6 text-red-500" />{L("Disciplinary Measures", "Mesures Disciplinaires")}</h2>
        <p className="text-muted-foreground text-sm">{isAdmin ? L("Record official measures and notify the parent. Teacher reports appear here as « pending » for you to action.", "Enregistrez les mesures officielles et notifiez le parent. Les signalements des enseignants apparaissent ici « en attente ».") : L("Report an incident to the administration. All communication to the parent goes through the admin — you cannot email parents directly.", "Signalez un incident à l'administration. Toute communication au parent passe par l'administration — vous ne pouvez pas contacter les parents directement.")}</p>
      </div>

      <div className="mb-6 max-w-md">
        <Select value={sid} onValueChange={setSid}>
          <SelectTrigger data-testid="disc-student-select"><SelectValue placeholder={L("Select student", "Choisir un élève")} /></SelectTrigger>
          <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} · {s.grade_level}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!sid ? <Empty title={L("Select a student", "Choisir un élève")} /> : (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-card border border-border rounded-lg p-5">
              <p className="font-heading font-semibold mb-3">{isAdmin ? L("New measure", "Nouvelle mesure") : L("Report an incident", "Signaler un incident")} — {selectedStudent?.first_name} {selectedStudent?.last_name}</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">{L("Reason", "Motif")}</label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="disc-category-select"><SelectValue placeholder={L("Choose reason", "Choisir le motif")} /></SelectTrigger>
                    <SelectContent>{CATS.map((c) => <SelectItem key={c.v} value={c.v}>{L(c.en, c.fr)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {isAdmin && (
                  <div>
                    <label className="text-xs text-muted-foreground">{L("Action", "Action")}</label>
                    <Select value={action} onValueChange={setAction}>
                      <SelectTrigger data-testid="disc-action-select"><SelectValue placeholder={L("Action", "Action")} /></SelectTrigger>
                      <SelectContent>{ACTIONS.map((a) => <SelectItem key={a.v} value={a.v}>{L(a.en, a.fr)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {isAdmin && action === "suspension" && (
                  <div>
                    <label className="text-xs text-muted-foreground">{L("Suspension days", "Jours de suspension")}</label>
                    <Input type="number" min={1} max={3} value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="disc-days-input" />
                  </div>
                )}
              </div>

              {rec && (
                <div className={`mt-4 rounded-md border p-3 flex items-start gap-2 ${ACTION_STYLE[rec.recommended_action]}`} data-testid="disc-recommendation">
                  <AlertTriangle className="h-4 w-4 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold">{L("Policy recommends", "La politique recommande")}: {actLabel(rec.recommended_action)}</p>
                    <p className="opacity-90">{rec.reason}</p>
                  </div>
                </div>
              )}

              <div className="mt-3">
                <label className="text-xs text-muted-foreground">{L("Notes (optional)", "Notes (optionnel)")}</label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="disc-notes-input" />
              </div>

              <div className="mt-4 flex items-center justify-between">
                {isAdmin ? (
                  <label className="flex items-center gap-2 text-sm"><Switch checked={notify} onCheckedChange={setNotify} data-testid="disc-notify-switch" />{L("Notify parent by email", "Notifier le parent par email")}</label>
                ) : (
                  <span className="text-xs text-muted-foreground max-w-xs" data-testid="disc-teacher-note">{L("This report goes only to the administration. Only an admin can notify the parent.", "Ce signalement va uniquement à l'administration. Seul un admin peut notifier le parent.")}</span>
                )}
                <Button onClick={submit} disabled={busy} data-testid="disc-submit-btn"><Send className="h-4 w-4 mr-1" />{isAdmin ? L("Record measure", "Enregistrer la mesure") : L("Report to administration", "Signalez à l'administration")}</Button>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-5">
              <p className="font-heading font-semibold mb-3">{L("History", "Historique")}</p>
              {records.length === 0 ? <p className="text-sm text-muted-foreground">{L("No measures on file.", "Aucune mesure au dossier.")}</p> : (
                <Table>
                  <TableHeader><TableRow><TableHead>{L("Date", "Date")}</TableHead><TableHead>{L("Reason", "Motif")}</TableHead><TableHead>{L("Action", "Action")}</TableHead><TableHead>{L("Status", "Statut")}</TableHead><TableHead className="text-right">{L("Letter", "Avis")}</TableHead></TableRow></TableHeader>
                  <TableBody>{records.map((r) => (
                    <TableRow key={r.id} data-testid={`disc-row-${r.id}`}>
                      <TableCell className="text-xs">{(r.created_at || "").slice(0, 10)}</TableCell>
                      <TableCell>{catLabel(r.category)}</TableCell>
                      <TableCell>{r.action ? <Badge variant="outline" className={ACTION_STYLE[r.action]}>{actLabel(r.action)}{r.suspension_days ? ` (${r.suspension_days}j)` : ""}</Badge> : <span className="text-xs text-muted-foreground">{L("proposed", "proposé")}: {actLabel(r.recommended_action)}</span>}</TableCell>
                      <TableCell>{r.status === "recorded" ? <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />{L("recorded", "enregistré")}{r.notified ? " ·📨" : ""}</span> : <span className="text-xs text-amber-600">{L("pending admin", "en attente")}</span>}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {r.status === "recorded" && <Button size="sm" variant="outline" onClick={() => letter(r)} data-testid={`disc-letter-${r.id}`}><FileDown className="h-3.5 w-3.5 mr-1" />{L("Letter", "Avis")}</Button>}
                        {isAdmin && r.status === "proposed" && <Button size="sm" onClick={() => approve(r)} data-testid={`disc-approve-${r.id}`}><Send className="h-3.5 w-3.5 mr-1" />{L("Notify parent", "Notifiez le Parent")}</Button>}
                        {isAdmin && <Button size="icon" variant="ghost" onClick={() => del(r)} data-testid={`disc-del-${r.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div className="bg-primary text-primary-foreground rounded-lg p-5">
              <p className="font-heading font-bold">{L("This year", "Cette année")}</p>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between"><span className="opacity-80">{L("Warnings", "Avertissements")}</span><span className="font-bold" data-testid="disc-sum-warnings">{summary?.warnings_count ?? 0}/3</span></div>
                <div className="flex justify-between"><span className="opacity-80">{L("Lateness", "Retards")}</span><span className="font-bold" data-testid="disc-sum-lateness">{summary?.lateness_count ?? 0}</span></div>
                <div className="flex justify-between"><span className="opacity-80">{L("Suspension days", "Jours de suspension")}</span><span className="font-bold" data-testid="disc-sum-suspension">{summary?.suspension_days_total ?? 0}/3</span></div>
              </div>
              <p className="mt-3 text-xs text-accent">{L("3 warnings → suspension · >3 suspension days → expulsion", "3 avertissements → suspension · >3 jours → expulsion")}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
